import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { authHeaders } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOOD_IMAGE_PRESETS = {
  pizza: "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80",
  burger: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=1200&q=80",
  tacos: "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?auto=format&fit=crop&w=1200&q=80",
  cafe: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80",
  sushi: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=1200&q=80",
  italian: "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=1200&q=80",
  mexican: "https://images.unsplash.com/photo-1565299585323-38174c4a6471?auto=format&fit=crop&w=1200&q=80",
  steakhouse: "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=80",
  default: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1200&q=80",
};

const HANDLE_RE = /^[A-Za-z0-9._]{1,30}$/;

const SOURCE_BADGE = {
  Instagram: "bg-pink-500/15 border-pink-500/30 text-pink-200",
  "Google Places": "bg-blue-500/15 border-blue-500/30 text-blue-200",
  Website: "bg-amber-500/15 border-amber-500/30 text-amber-200",
  "TapToTable Generated": "bg-cyan-500/15 border-cyan-500/30 text-cyan-200",
  Fallback: "bg-zinc-500/15 border-zinc-500/30 text-zinc-300",
};

function SourceTag({ source }) {
  if (!source) return null;
  const cls = SOURCE_BADGE[source] || SOURCE_BADGE.Fallback;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-medium uppercase tracking-wider rounded-full border px-2 py-0.5 ${cls}`}
      data-testid={`draft-source-${source.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {source}
    </span>
  );
}

function Field({ label, source, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs text-zinc-400">{label}</label>
        <SourceTag source={source} />
      </div>
      <div className="mt-1">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}

const EVENT_HOURS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1)
);
const EVENT_MINUTES = ["00", "15", "30", "45"];
const EVENT_MERIDIEMS = ["AM", "PM"];

function parseDraftEventTime(value = "") {
  const raw = String(value || "").trim();

  const result = {
    startHour: "6",
    startMinute: "00",
    startMeridiem: "PM",
    endHour: "7",
    endMinute: "00",
    endMeridiem: "PM",
  };

  if (!raw) return result;

  const matches = [...raw.matchAll(
    /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/gi
  )];

  const apply = (match, prefix) => {
    if (!match) return;

    let hour = Math.max(1, Math.min(12, Number(match[1]) || 1));
    const minute = EVENT_MINUTES.includes(match[2])
      ? match[2]
      : "00";
    const period = String(match[3] || "PM").toUpperCase();

    result[`${prefix}Hour`] = String(hour);
    result[`${prefix}Minute`] = minute;
    result[`${prefix}Meridiem`] =
      EVENT_MERIDIEMS.includes(period) ? period : "PM";
  };

  apply(matches[0], "start");

  if (matches[1]) {
    apply(matches[1], "end");
  } else {
    let nextHour = Number(result.startHour) + 1;
    if (nextHour > 12) nextHour = 1;
    result.endHour = String(nextHour);
    result.endMinute = result.startMinute;
    result.endMeridiem = result.startMeridiem;
  }

  return result;
}

function buildDraftEventTime(parts = {}) {
  return `${parts.startHour}:${parts.startMinute} ${parts.startMeridiem} – ${parts.endHour}:${parts.endMinute} ${parts.endMeridiem}`;
}

function updateDraftEventTime(value = "", key, nextValue) {
  const parts = parseDraftEventTime(value);
  parts[key] = nextValue;
  return buildDraftEventTime(parts);
}


function looksLikeEventStrategy(strategy = {}, strategyDraft = {}) {
  const haystack = [
    strategy?.strategy_name,
    strategy?.verified_event,
    strategyDraft?.event_name,
    strategyDraft?.headline,
    strategyDraft?.offer,
    strategyDraft?.short_description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /\b(event|tasting|trivia(?: night)?|live music|live magic|magic show|karaoke|wine dinner|chef['’]?s dinner|community night|family night)\b/i.test(
    haystack
  );
}

function cleanEventName(value = "") {
  return String(value || "")
    .replace(/\s+Promotion$/i, "")
    .replace(/\s+Event$/i, " Event")
    .trim();
}

function deriveEventSchedule(value = "") {
  const text = String(value || "").trim();
  if (!text) return { eventDate: "", eventTime: "" };

  const parts = text.split(" · ").map((part) => part.trim()).filter(Boolean);

  if (parts.length >= 2) {
    const tail = parts[parts.length - 1];

    if (/\b(mon|tue|wed|thu|fri|sat|sun)(day)?\b/i.test(tail)) {
      return { eventDate: tail, eventTime: "" };
    }

    if (/\b\d{1,2}(?::\d{2})?\s*(am|pm)\b/i.test(tail)) {
      return { eventDate: "", eventTime: tail };
    }
  }

  return { eventDate: "", eventTime: "" };
}


/**
 * Two-step admin flow:
 *  step="handle"  → enter Instagram handle, hit Generate
 *  step="review"  → editable draft form. ✨ Improve copy → optional LLM
 *                  Create Demo → POST /api/mockups (existing endpoint)
 *
 * No DB writes happen until Create Demo is clicked. Each modal instance is
 * fully isolated; concurrent admins can run drafts in parallel.
 */
export default function GenerateDraftModal({ onClose, onCreated, assignedUser = null }) {
  const [step, setStep] = useState("handle");
  const [handle, setHandle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [sources, setSources] = useState({});
  const [confidence, setConfidence] = useState({});
  const [offerPresets, setOfferPresets] = useState([]);
  const [themePresets, setThemePresets] = useState({});
  const [improving, setImproving] = useState(false);
  const handleRef = useRef(null);

  useEffect(() => {
    handleRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting && !improving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting, improving]);

  const generate = async (e) => {
    e?.preventDefault();
    setError("");
    const h = handle.trim().replace(/^@/, "");
    if (!h || !HANDLE_RE.test(h)) {
      setError("Enter a valid Instagram handle (letters, numbers, dots, underscores).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/mockups/generate-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ handle: h }),
      });
      if (res.status === 401) {
        setError("Admin key rejected.");
        return;
      }
      if (res.status === 503) {
        const j = await res.json().catch(() => ({}));
        setError(j.detail || "Apify is not configured on the server.");
        return;
      }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.detail || `HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      if (!data.ok) {
        setError(data.message || "Could not generate draft.");
        return;
      }
      const generatedStrategies = data.strategies || [];
      setStrategies(generatedStrategies);
      setDraft(data.draft);
      setSources(data.sources || {});
      setConfidence(data.confidence || {});
      setOfferPresets([
      data.draft?.selectedOffer,
      ...generatedStrategies.map((s) => (s.draft || s).offer),
      ...(data.offer_presets || []),
     ].filter(Boolean));
     setThemePresets(data.theme_presets || {});
     setStep(generatedStrategies.length ? "strategies" : "review");
    } catch (err) {
      console.error(err);
      setError("Network error. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseStrategy = (strategy) => {
  if (!strategy) return;

 const strategyDraft = strategy.draft || strategy;
 const isEventStrategy =
   strategy.campaign_type === "event" ||
   strategy.page_type === "event" ||
   strategyDraft.campaign_type === "event" ||
   strategyDraft.page_type === "event" ||
   looksLikeEventStrategy(strategy, strategyDraft);

 const inferredEventName = cleanEventName(
   strategyDraft.event_name ||
   strategy.verified_event ||
   strategy.strategy_name ||
   strategyDraft.offer ||
   ""
 );

 const inferredSchedule = deriveEventSchedule(
   strategyDraft.offer ||
   strategyDraft.event_name ||
   strategy.strategy_name ||
   ""
 );

setDraft((d) => ({
  ...d,
  headline: isEventStrategy
    ? `${inferredEventName} at ${d.businessName || "the restaurant"}`
    : (strategyDraft.headline || d.headline),
  shortDescription: isEventStrategy
    ? `Join ${d.businessName || "the restaurant"} for ${inferredEventName}. View the event details and make your plans.`
    : (strategyDraft.short_description || d.shortDescription),
  selectedOffer: isEventStrategy ? "" : (strategyDraft.offer || d.selectedOffer),
  ctaText: isEventStrategy ? "" : (strategyDraft.cta_text || d.ctaText),
  privateNotes: strategyDraft.private_notes || d.privateNotes,
  campaignType: isEventStrategy ? "event" : "offer",
  pageType: isEventStrategy ? "event" : "promotion",
  eventName: isEventStrategy
    ? inferredEventName
    : "",
  eventDate: isEventStrategy
    ? (strategyDraft.event_date || inferredSchedule.eventDate || "")
    : "",
  eventTime: isEventStrategy
    ? (strategyDraft.event_time || inferredSchedule.eventTime || "")
    : "",
  eventDescription: isEventStrategy
    ? (
        strategyDraft.event_description ||
        strategy.why ||
        (inferredEventName
          ? `See event details for ${inferredEventName}.`
          : "")
      )
    : "",
  eventActionText: isEventStrategy
    ? (strategyDraft.event_action_text || "View Event Details")
    : "",
  eventActionLink: isEventStrategy
    ? (strategyDraft.event_action_link || d.websiteUrl || "")
    : "",
}));

  setSources((current) => ({
    ...current,
    selectedOffer: "TapToTable Generated",
    headline: "TapToTable Generated",
    shortDescription: "TapToTable Generated",
    ctaText: "TapToTable Generated",
    privateNotes: "TapToTable Generated",
  }));

  setStep("review");
};
  
  const improveCopy = async () => {
    if (!draft) return;
    setImproving(true);
    try {
      const res = await fetch(`${API}/mockups/improve-copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          business_name: draft.businessName,
          category: draft.category,
          selected_offer: draft.selectedOffer,
          bio: draft.bio || "",
          website_text: draft.shortDescription || "",
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Could not improve copy");
        return;
      }
      const c = await res.json();
      setDraft((d) => ({
        ...d,
        headline: c.headline || d.headline,
        shortDescription: c.short_description || d.shortDescription,
        ctaText: c.cta_text || d.ctaText,
        privateNotes: c.private_notes || d.privateNotes,
      }));
      // After LLM improvement, copy fields are no longer "Fallback".
      setSources((s) => ({
        ...s,
        headline: "AI",
        shortDescription: "AI",
        ctaText: "AI",
        privateNotes: "AI",
      }));
      toast.success("Copy improved");
    } catch (err) {
      console.error(err);
      toast.error("LLM call failed");
    } finally {
      setImproving(false);
    }
  };

  const createDemo = async () => {
    if (!draft) return;

    if (
      draft.campaignType === "event" &&
      !/^\d{4}-\d{2}-\d{2}$/.test(draft.eventDate || "")
    ) {
      setError("Choose the actual event date before creating the Event Page.");
      return;
    }

    if (
      draft.campaignType !== "event" &&
      draft.selectedOffer === "Custom offer" &&
      !draft.customOffer?.trim()
    ) {
      setError("Enter your custom offer before creating the Restaurant Page.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const themeColor = draft.themeColors?.primary || "";
      const body = {
        restaurant_name: draft.businessName.trim(),
        instagram_handle: (draft.instagramHandle || "").trim() || null,
        city: draft.city || null,
        cuisine: draft.category || null,
        hero_image_url: draft.heroImageUrl || null,
        website_url: draft.websiteUrl || null,
        menu_url: null,
        address: draft.address || null,
        phone: draft.phone || null,
        hours: draft.hours || null,
        offer:
          draft.selectedOffer === "Custom offer"
            ? draft.customOffer.trim()
            : draft.selectedOffer || null,

        description: draft.shortDescription || null,
        cta_text: draft.ctaText || null,
        cta_link: draft.websiteUrl || null,
        brand_color: themeColor || null,
        rating: draft.rating || null,
        rating_count: draft.ratingCount || null,
        theme: draft.themeKey || null,
        notes: draft.privateNotes || null,

        campaign_type: draft.campaignType || "offer",
        event_name:
          draft.campaignType === "event" ? (draft.eventName || "") : "",
        event_date:
          draft.campaignType === "event" ? (draft.eventDate || "") : "",
        event_time:
          draft.campaignType === "event" ? (draft.eventTime || "") : "",
        event_description:
          draft.campaignType === "event" ? (draft.eventDescription || "") : "",
        event_action_text:
          draft.campaignType === "event"
            ? (draft.eventActionText || "View Event Details")
            : "",
        event_action_link:
          draft.campaignType === "event"
            ? (draft.eventActionLink || draft.websiteUrl || "")
            : "",
        
        assigned_to: assignedUser?.id || "owner",
        owner_id: assignedUser?.id || "owner",
        owner_email: assignedUser?.email || "",
      };
      // Strip nulls — backend MockupBase fields are Optional and will accept
      // missing keys.
      Object.keys(body).forEach((k) => body[k] == null && delete body[k]);
      const res = await fetch(`${API}/mockups`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.detail?.[0]?.msg || j.detail || `HTTP ${res.status}`);
        return;
      }
      const created = await res.json();
      toast.success("Restaurant Page created");
      onCreated(created);
    } catch (err) {
      console.error(err);
      setError("Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const getCuisineFoodImage = (category = "") => {
  const c = category.toLowerCase();

  if (c.includes("pizza")) return FOOD_IMAGE_PRESETS.pizza;
  if (c.includes("burger")) return FOOD_IMAGE_PRESETS.burger;
  if (c.includes("taco") || c.includes("mex")) return FOOD_IMAGE_PRESETS.mexican;
  if (c.includes("cafe") || c.includes("coffee")) return FOOD_IMAGE_PRESETS.cafe;
  if (c.includes("sushi") || c.includes("japanese")) return FOOD_IMAGE_PRESETS.sushi;
  if (c.includes("ital")) return FOOD_IMAGE_PRESETS.italian;
  if (c.includes("steak")) return FOOD_IMAGE_PRESETS.steakhouse;

  return FOOD_IMAGE_PRESETS.default;
};

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="generate-draft-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting && !improving) onClose();
      }}
    >
      <div className="w-full max-w-2xl bg-[#121214] border border-white/10 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00E5FF]">
              Generate Restaurant Page Draft
            </div>
            <h2 className="font-display font-semibold text-xl mt-1">
              {step === "handle" ? "Enter Instagram handle" : "Review & edit draft"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || improving}
            className="text-zinc-400 hover:text-white text-xl px-2 disabled:opacity-50"
            data-testid="generate-draft-close"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {step === "handle" && (
          <form onSubmit={generate} noValidate className="mt-5">
            <label className="block text-xs text-zinc-400">
              Instagram handle <span className="text-[#00E5FF]">*</span>
            </label>
            <div className="mt-1 flex items-center bg-[#1A1A1F] border border-white/10 rounded-xl focus-within:border-[#00E5FF] focus-within:ring-1 focus-within:ring-[#00E5FF]">
              <span className="pl-4 text-zinc-500">@</span>
              <input
                ref={handleRef}
                type="text"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="restaurantname"
                className="flex-1 bg-transparent px-2 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none"
                data-testid="draft-handle-input"
              />
            </div>
            <div className="mt-2 text-[11px] text-zinc-500">
              Public profiles only. We use Apify to read what's already visible on the
              public profile page — no Instagram credentials needed.
            </div>
            {error && (
              <div
                className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                data-testid="draft-error"
              >
                {error}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="text-xs text-zinc-400 hover:text-white px-4 py-2 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#00E5FF] text-black font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-[#66FFFF] transition-colors disabled:opacity-60"
                data-testid="draft-generate-submit"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full border-2 border-black border-t-transparent animate-spin" />
                    Generating…
                  </span>
                ) : (
                  "Generate draft"
                )}
              </button>
            </div>
          </form>
        )}

 {step === "strategies" && draft && (
  <div className="mt-5 space-y-4" data-testid="strategy-select">
    <div>
      <h3 className="text-lg font-bold text-white">Choose a Marketing Campaign</h3>
      <p className="mt-1 text-sm text-zinc-400">
        Pick the direction that best fits this restaurant. You can still edit everything before creating the Restaurant Page.
      </p>
    </div>

    <div className="grid gap-3">
      {strategies.map((strategy, index) => {
        const strategyDraft = strategy.draft || strategy;
        const isEventStrategy =
          strategy.campaign_type === "event" ||
          strategy.page_type === "event" ||
          strategyDraft.campaign_type === "event" ||
          strategyDraft.page_type === "event" ||
          looksLikeEventStrategy(strategy, strategyDraft);

        return (
          <div
            key={`${strategyDraft.offer || "strategy"}-${index}`}
            className="rounded-xl border border-white/10 bg-zinc-900/70 p-4"
          >
        <div className="flex items-start justify-between gap-4">

  <div className="flex-1">

    <h3 className="text-2xl font-bold text-white leading-tight">
      {strategy.strategy_name || `Strategy ${String.fromCharCode(65 + index)}`}
    </h3>

    {isEventStrategy && (
      <div className="mt-2 inline-flex items-center rounded-full bg-violet-500/15 border border-violet-500/30 px-2 py-1 text-[11px] font-semibold text-violet-200">
        EVENT PAGE
      </div>
    )}

    {index === 0 && (
  <div className="mt-2 inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-1 text-[11px] font-semibold text-emerald-300">
    ★ Recommended Campaign
  </div>
)}

    <div className="mt-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">
        Goal
      </div>

      <h4 className="mt-1 text-xl font-bold text-white leading-tight">
        {strategy.goal || strategyDraft.headline}
      </h4>
    </div>

  </div>

  {typeof strategy.confidence === "number" && (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-center">
  <div className="text-[10px] uppercase tracking-wider text-emerald-300">
    Confidence
  </div>
  <div className="text-2xl font-extrabold text-white">
    {Math.round(strategy.confidence * 100)}%
  </div>
</div>
  )}

</div>
            
            {strategy.why && (
              <div className="mt-3 rounded-lg bg-white/5 p-3">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                  Why this strategy works
                </div>
                <p className="mt-1 text-xs text-zinc-300">
                  {strategy.why}
                </p>
              </div>
            )}

            <div className="mt-4 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                  {isEventStrategy ? "Event Details" : "Offer"}
                </div>
                <div className="mt-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-xl font-extrabold text-white">
                  {isEventStrategy
                    ? (
                        strategyDraft.event_card_summary ||
                        strategyDraft.event_description ||
                        [
                          strategyDraft.event_name || strategy.verified_event,
                          strategyDraft.event_date,
                          strategyDraft.event_time,
                        ].filter(Boolean).join(" · ")
                      )
                    : strategyDraft.offer}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                  {isEventStrategy ? "Event Button" : "Claim Button"}
                </div>
                <div className="mt-1 text-white">
                  {isEventStrategy
                    ? (strategyDraft.event_action_text || "View Event Details")
                    : strategyDraft.cta_text}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                  Campaign Badge
                </div>
                <div className="mt-1 text-white">
                  {(strategyDraft.badge || "")
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleUseStrategy(strategy)}
              className="mt-4 w-full rounded-xl bg-cyan-400 px-4 py-3 text-sm font-bold text-black hover:bg-cyan-300"
            >
              Use This Strategy
            </button>
          </div>
        );
      })}
    </div>
  </div>
)}
        {step === "review" && draft && (
          <div className="mt-5 space-y-5" data-testid="draft-review">
            {/* warning banner */}
            {(!confidence.addressVerified ||
              !confidence.phoneVerified ||
              !confidence.websiteVerified) && (
              <div
                className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
                data-testid="draft-unverified-warning"
              >
                Heads up — these fields are unverified:{" "}
                {[
                  !confidence.addressVerified && "address",
                  !confidence.phoneVerified && "phone",
                  !confidence.websiteVerified && "website",
                ]
                  .filter(Boolean)
                  .join(", ")}
                . Spot-check before sending.
              </div>
            )}

            {/* Image preview */}
            {draft.heroImageUrl && (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label className="text-xs text-zinc-400">Hero image preview</label>
                  <SourceTag source={draft.imageSource} />
                </div>
                <div className="mt-1 aspect-[4/3] w-full rounded-xl overflow-hidden border border-white/10 bg-[#1A1A1F]">
                  <img
                    src={draft.heroImageUrl}
                    alt={draft.businessName}
                    className="h-full w-full object-cover"
                    onError={() => {
                      setDraft((d) => ({
                        ...d,
                        heroImageUrl: getCuisineFoodImage(d.category),
                        imageSource: "Fallback",
                      }));
                    }}
                  />
                </div>
                <div className="mt-2 break-all text-[11px] text-zinc-500">
  Image URL: {draft.heroImageUrl}
</div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Business name" source={sources.businessName}>
                <input
                  type="text"
                  value={draft.businessName || ""}
                  onChange={(e) => updateField("businessName", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-businessName"
                />
              </Field>
              <Field label="Instagram handle" source="Instagram">
                <input
                  type="text"
                  value={draft.instagramHandle || ""}
                  onChange={(e) => updateField("instagramHandle", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-instagramHandle"
                />
              </Field>
              <Field label="Website" source={sources.websiteUrl}>
                <input
                  type="url"
                  value={draft.websiteUrl || ""}
                  onChange={(e) => updateField("websiteUrl", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-websiteUrl"
                />
              </Field>
              <Field label="Phone" source={sources.phone}>
                <input
                  type="tel"
                  value={draft.phone || ""}
                  onChange={(e) => updateField("phone", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-phone"
                />
              </Field>
              <Field label="Address" source={sources.address}>
                <input
                  type="text"
                  value={draft.address || ""}
                  onChange={(e) => updateField("address", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-address"
                />
              </Field>
              <Field label="City" source={sources.address}>
                <input
                  type="text"
                  value={draft.city || ""}
                  onChange={(e) => updateField("city", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-city"
                />
              </Field>
              <Field label="Category" source={sources.category}>
                <input
                  type="text"
                  value={draft.category || ""}
                  onChange={(e) => updateField("category", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-category"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field
                  label="Hours"
                  source={sources.hours}
                  hint="Weekly hours from Google Places. Review before creating the Restaurant Page."
                >
                  <textarea
                    rows={5}
                    value={(draft.hours || "").replace(/ · /g, "\n")}
                    onChange={(e) =>
                      updateField(
                        "hours",
                        e.target.value.replace(/\n+/g, " · ")
                      )
                    }
                    wrap="soft"
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm leading-6 text-white focus:outline-none focus:border-[#00E5FF] resize-y whitespace-pre-wrap overflow-x-hidden"
                    data-testid="draft-hours"
                  />
                </Field>
              </div>
            </div>

            {draft.campaignType === "event" ? (
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4 space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-violet-200">
                  Event Page
                </div>

                <Field label="Event Name" source="TapToTable Generated">
                  <input
                    type="text"
                    value={draft.eventName || ""}
                    onChange={(e) => updateField("eventName", e.target.value)}
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="draft-eventName"
                  />
                </Field>

                <Field
                  label="Event Date"
                  hint="Choose the actual calendar date. A weekday-only source such as Wednesday is not saved as a date."
                >
                  <input
                    type="date"
                    value={
                      /^\d{4}-\d{2}-\d{2}$/.test(draft.eventDate || "")
                        ? draft.eventDate
                        : ""
                    }
                    onChange={(e) => updateField("eventDate", e.target.value)}
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF] [color-scheme:dark]"
                    data-testid="draft-eventDate"
                  />
                </Field>

                <Field
                  label="Event Time"
                  hint={draft.eventTime || "Choose a start and end time."}
                >
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      ["Starts", "startHour", "startMinute", "startMeridiem"],
                      ["Ends", "endHour", "endMinute", "endMeridiem"],
                    ].map(([label, hourKey, minuteKey, meridiemKey]) => {
                      const parsed = parseDraftEventTime(draft.eventTime || "");

                      return (
                        <div
                          key={label}
                          className="rounded-xl border border-violet-400/20 bg-black/15 p-3"
                        >
                          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-violet-200">
                            {label}
                          </div>

                          <div className="grid grid-cols-[1fr_1.15fr_1fr] gap-2">
                            <select
                              value={parsed[hourKey]}
                              onChange={(e) =>
                                updateField(
                                  "eventTime",
                                  updateDraftEventTime(
                                    draft.eventTime || "",
                                    hourKey,
                                    e.target.value
                                  )
                                )
                              }
                              className="min-h-[44px] min-w-0 w-full rounded-lg border border-white/10 bg-[#1A1A1F] px-2 text-center text-sm font-semibold text-white [color-scheme:dark]"
                            >
                              {EVENT_HOURS.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </select>

                            <select
                              value={parsed[minuteKey]}
                              onChange={(e) =>
                                updateField(
                                  "eventTime",
                                  updateDraftEventTime(
                                    draft.eventTime || "",
                                    minuteKey,
                                    e.target.value
                                  )
                                )
                              }
                              className="min-h-[44px] min-w-0 w-full rounded-lg border border-white/10 bg-[#1A1A1F] px-2 text-center text-sm font-semibold text-white [color-scheme:dark]"
                            >
                              {EVENT_MINUTES.map((minute) => (
                                <option key={minute} value={minute}>
                                  :{minute}
                                </option>
                              ))}
                            </select>

                            <select
                              value={parsed[meridiemKey]}
                              onChange={(e) =>
                                updateField(
                                  "eventTime",
                                  updateDraftEventTime(
                                    draft.eventTime || "",
                                    meridiemKey,
                                    e.target.value
                                  )
                                )
                              }
                              className="min-h-[44px] min-w-0 w-full rounded-lg border border-white/10 bg-[#1A1A1F] px-2 text-center text-sm font-semibold text-white [color-scheme:dark]"
                            >
                              {EVENT_MERIDIEMS.map((period) => (
                                <option key={period} value={period}>
                                  {period}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Field>

                <Field
                  label="Event Description"
                  source="TapToTable Generated"
                  hint="This is the fuller Event Page description. The strategy card uses a shorter preview."
                >
                  <textarea
                    rows={3}
                    value={draft.eventDescription || ""}
                    onChange={(e) => updateField("eventDescription", e.target.value)}
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF] resize-y"
                    data-testid="draft-eventDescription"
                  />
                </Field>

                <Field label="Event Button Text" source="TapToTable Generated">
                  <input
                    type="text"
                    value={draft.eventActionText || ""}
                    onChange={(e) => updateField("eventActionText", e.target.value)}
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="draft-eventActionText"
                  />
                </Field>

                <Field
                  label="Event Button Link"
                  hint="Defaults to the restaurant website when no event-specific link was found."
                >
                  <input
                    type="url"
                    value={draft.eventActionLink || ""}
                    onChange={(e) => updateField("eventActionLink", e.target.value)}
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="draft-eventActionLink"
                  />
                </Field>
              </div>
            ) : (

<Field
  label="Offer"
  source={sources.selectedOffer}
  hint={`Confidence: ${Math.round((confidence.selectedOffer || 0) * 100)}%`}
>
  <select
    value={draft.selectedOffer || ""}
    onChange={(e) =>
      updateField("selectedOffer", e.target.value)
    }
    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
    data-testid="draft-selectedOffer"
  >
    {offerPresets
      .filter((offer) => offer !== "Custom offer")
      .map((offer) => (
        <option key={offer} value={offer}>
          {offer}
        </option>
      ))}
    <option value="Custom offer">Custom offer</option>
  </select>

  {draft.selectedOffer === "Custom offer" && (
    <div className="mt-3">
      <label className="mb-2 block text-xs font-semibold text-cyan-300">
        Enter Your Custom Offer
      </label>
      <textarea
        rows={2}
        maxLength={500}
        value={draft.customOffer || ""}
        onChange={(e) =>
          updateField("customOffer", e.target.value)
        }
        placeholder="Enter your promotion or featured item..."
        className="w-full resize-y rounded-lg border border-cyan-400/40 bg-[#1A1A1F] px-3 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5FF]"
        data-testid="draft-customOffer"
      />
      <p className="mt-1 text-[11px] text-zinc-500">
        This wording will be used on the Restaurant Page.
      </p>
    </div>
  )}
</Field>

            )}

            {/* Theme dropdown */}
            <Field
              label="Theme"
              source={sources.themeKey}
              hint={`Primary: ${draft.themeColors?.primary || "—"}`}
            >
              <select
                value={draft.themeKey || "default"}
                onChange={(e) => {
                  const k = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    themeKey: k,
                    themeColors: themePresets[k] || d.themeColors,
                  }));
                }}
                className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                data-testid="draft-themeKey"
              >
                {Object.entries(themePresets).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.name}
                  </option>
                ))}
              </select>
            </Field>

            {/* Copy block with ✨ Improve copy */}
            <div className="rounded-xl border border-white/10 bg-[#0F0F12] p-4 space-y-3">
              <div className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">
                {draft.campaignType === "event"
                  ? "Event marketing copy"
                  : "Marketing copy"}
              </div>
              <Field
                label={draft.campaignType === "event" ? "Event Headline" : "Headline"}
                source={sources.headline}
              >
                <input
                  type="text"
                  value={draft.headline || ""}
                  onChange={(e) => updateField("headline", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                  data-testid="draft-headline"
                />
              </Field>
              <Field
                label={draft.campaignType === "event" ? "Event Summary" : "Short description"}
                source={sources.shortDescription}
              >
                <textarea
                  rows={2}
                  value={draft.shortDescription || ""}
                  onChange={(e) => updateField("shortDescription", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF] resize-none"
                  data-testid="draft-shortDescription"
                />
              </Field>
              {draft.campaignType !== "event" && (
                <Field label="Claim Button Text" source={sources.ctaText}>
                  <input
                    type="text"
                    value={draft.ctaText || ""}
                    onChange={(e) => updateField("ctaText", e.target.value)}
                    className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]"
                    data-testid="draft-ctaText"
                  />
                </Field>
              )}
              <Field label="Private notes (internal)" source={sources.privateNotes}>
                <textarea
                  rows={2}
                  value={draft.privateNotes || ""}
                  onChange={(e) => updateField("privateNotes", e.target.value)}
                  className="w-full bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF] resize-none"
                  data-testid="draft-privateNotes"
                />
              </Field>
            </div>

            {(draft.followerCount || draft.rating) && (
              <div className="text-xs text-zinc-500">
                {draft.followerCount > 0 && (
                  <>Followers: <span className="text-zinc-300">{draft.followerCount.toLocaleString()}</span></>
                )}
                {draft.rating && (
                  <>
                    {" · "}Rating: <span className="text-zinc-300">{draft.rating}★</span>
                    {draft.ratingCount && <> ({draft.ratingCount})</>}
                  </>
                )}
              </div>
            )}

            {error && (
              <div
                className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                data-testid="draft-error"
              >
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStep("handle")}
                disabled={submitting || improving}
                className="text-xs text-zinc-400 hover:text-white px-3 py-2 disabled:opacity-50"
                data-testid="draft-back"
              >
                ← Try another handle
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting || improving}
                  className="text-xs text-zinc-400 hover:text-white px-4 py-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={createDemo}
                  disabled={submitting || improving}
                  className="bg-[#00E5FF] text-black font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-[#66FFFF] transition-colors disabled:opacity-60"
                  data-testid="draft-create-demo"
                >
                  {submitting
                    ? "Creating…"
                    : draft.campaignType === "event"
                      ? "Create Event Page"
                      : "Create Restaurant Page"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
